import { useEffect, useLayoutEffect, useState, useRef, Suspense } from 'react';
import { Route, Routes } from 'react-router-dom';
import './App.scss';
import { useProSidebar } from 'react-pro-sidebar';
import { deleteCookie, getDecodedToken, getAlerts } from './service/service';
import moment from 'moment-timezone';
import IconButton from '@mui/material/IconButton';
import { ReactComponent as ExitFullScreenIcon } from './assets/exit_fullscreen.svg';
import { OverflowTooltip } from './components/overflow_tooltip';
import { HeaderContext } from './Context/HeaderContext';
import { Alert, Badge, Box, Snackbar, Stack, Tooltip, Typography, Zoom } from '@mui/material';
import { ReactComponent as NotificationBellIcon } from './assets/notification.svg';
import RulesEngine from './components/RulesEngine/RulesEngine';
import { useRedirectLogin } from 'hooks/useRedirectHooks';
import AppRoutes from 'routes/routes';
import { useLocation } from "react-router-dom";

import Sidebar from './components/landingScreen/sidebar';
import Header from './components/landingScreen/header';
import Login from './components/loginScreen/loginScreen';
import FormLogin from './components/loginScreen/formLoginScreen';
import SideDrawer from 'components/SideDrawer';
import AskMeButton from './components/PepWiseAI/AskMeButton';
import GenieChatButton from 'components/FineOps/GenieChat';
import {
	setAuthenticated,
	setAuthToken,
	setAuthType,
	setCapabilities,
	setRole,
	setPrivilege,
	setSector,
	setSectorViewer,
	usePepwiseAuthController
} from 'Context/contexts/AuthContext';
import eventEmitter, { Severity } from 'SnackBarEventEmitter';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import SessionTimeoutDialog from 'utils/SessionTimeoutDialog';
// Lazy load components
// const Sidebar = lazy(() => import('./components/landingScreen/sidebar'));
// const Header = lazy(() => import('./components/landingScreen/header'));
// const Login = lazy(() => import('./components/loginScreen/loginScreen'));
// const FormLogin = lazy(() => import('./components/loginScreen/formLoginScreen'));
import { ReactComponent as ComingSoonIcon } from './assets/coming-soon-dp.svg'
import { ReactComponent as FullScreenIcon } from './assets/PepWise2.0/Full Screen.svg';
import NotificationIcon  from './assets/PepWise2.0/Layer 2.png';
import GlobalHeader from './components/LandingPage/Header';
import GlobalSidebar from 'components/landingScreen/GlobalSidebar';
import { initSearch } from 'components/GlobalSearch/init';

const synced = moment();

function App() {
	const url = useLocation();
	const dataFetchedRef = useRef(false);
	const [fullscreen, setFullScreen] = useState(false);
	const [originalRuleEngineData, setOriginalRuleEngineData] = useState<any>([]);
	const [isPanelOpen, setPanelOpen] = useState<boolean>(false);
	const [controller, dispatch] = usePepwiseAuthController();
	const { redirectToLogin, redirectAfterLogin } = useRedirectLogin();
	const getToken = () => document.cookie.split("; ").find(row => row.startsWith("id_token="));
	const [memberData, setMemberData] = useState<any>(null);

	const [dateString, setDateString] = useState('');

	const { isAuthenticated, accessToken } = controller;

	const [snackbarOpen, setSnackbarOpen] = useState(false);
	const [snackbarMessage, setSnackbarMessage] = useState('');
	const [dataCount, setDataCount] = useState('');
	const [snackbarSeverity, setSnackbarSeverity] = useState<Severity>('error');


	document.addEventListener('fullscreenchange', exitHandler);
	document.addEventListener('webkitfullscreenchange', exitHandler);
	document.addEventListener('mozfullscreenchange', exitHandler);
	document.addEventListener('MSFullscreenChange', exitHandler);

	// to display data
	useEffect(() => {
		// console.log(moment(sd).diff(end, 'days'))
		// const tokenData = getDecodedToken();
		setMemberData(accessToken);
	}, []);

	function exitHandler() {
		if (!document.fullscreenElement) {
			setFullScreen(false);
		}
	}

	moment.tz.setDefault('America/New_York');
	moment.defaultFormat = 'MM-DD-YYYY HH:mm:ss';
	const [showDialog, setShowDialog] = useState(false);
	const [countdown, setCountdown] = useState(30);
	const lastActivity = useRef(Date.now());
	const countdownInterval = useRef<number | null>(null);
	const idleInterval = useRef<number | null>(null);
	const location = useLocation(); // Get the current route

	const idleout = () => {
		deleteCookie('id_token');
		window.location.href = "/";
	};
	const handleCloseLogout = () => {
		setShowDialog(false);
	}

	const stayLoggedIn = () => {
		setShowDialog(false);
		setCountdown(30);
		lastActivity.current = Date.now();
		localStorage.setItem("lastActivity", Date.now().toString());

		if (countdownInterval.current !== null) {
			clearInterval(countdownInterval.current);
			countdownInterval.current = null;
		}
	};

	useEffect(() => {
		const saved = sessionStorage.getItem('pipelineRunStatusAutoRefresh');
		const interval = saved ? Number(saved) : 5;
		sessionStorage.setItem('pipelineRunStatusAutoRefresh', interval.toString());
		sessionStorage.setItem('pipelineRunStatusDateRangeTimeZone', 'CST');
		sessionStorage.setItem('pipelineRunStatusDateRangeType', 'today');
		sessionStorage.setItem('pipelineRunStatusStartDate', '');
		sessionStorage.setItem('pipelineRunStatusEndDate', '');
	}, []);

	useEffect(() => {
		[
			{ prefix: 'dataReconTransactional', defaultRange: '7d' },
			{ prefix: 'dataReconMaster', defaultRange: 'today' },
		].forEach(({ prefix, defaultRange }) => {
			sessionStorage.setItem(`${prefix}DateRangeType`, defaultRange);
			sessionStorage.setItem(`${prefix}DateRangeTimeZone`, 'CST');
			sessionStorage.removeItem(`${prefix}StartDate`);
			sessionStorage.removeItem(`${prefix}EndDate`);
		});
	}, []);

	useLayoutEffect(() => {
		document.body.classList.remove('jobs-list-datas', 'jobs-list-table', 'jobs-run-table');

		// Clear legacy layout classes only when entering Integrated Job Monitoring. Other dashboards use these classes for their own layouts.
		if (!url.pathname.startsWith('/dataOps/integratedJobMonitoring')) {
			return;
		}
		const root = document.getElementById('root');
		root?.classList.remove('jobs-list-table');
	}, [url.pathname]);

	useEffect(() => {
		if (!url.pathname.includes('/alertFramework') && !url.pathname.includes('/alert-details')) {
		localStorage.removeItem('alertFilters');
		}
	}, [url.pathname]);

	useEffect(() => {
		initSearch().catch(console.error);
	}, []);

	useEffect(() => {
		if (location.pathname === "/dashboard/sessionTimeOut") {
			return;
		}

		const checkIdle = () => {
			if (!getToken()) return;

			const storedTime = localStorage.getItem("lastActivity");
			if (storedTime) {
				lastActivity.current = parseInt(storedTime, 10);
			}

			const idleTime = (Date.now() - lastActivity.current) / 1000;
			if (idleTime >= 1770 && !showDialog) {
				setShowDialog(true);
				setCountdown(30);

				if (countdownInterval.current !== null) {
					clearInterval(countdownInterval.current);
				}

				countdownInterval.current = window.setInterval(() => {
					setCountdown((prev) => {
						if (prev === 1) {
							clearInterval(countdownInterval.current!);
							setShowDialog(false);
							window.location.href = "/dashboard/sessionTimeOut";
							return 0;
						}
						return prev - 1;
					});
				}, 1000);
			}
		};

		const activityListener = () => {
			lastActivity.current = Date.now();
			localStorage.setItem("lastActivity", Date.now().toString());
		};

		idleInterval.current = window.setInterval(checkIdle, 1000);
		window.addEventListener("mousemove", activityListener);
		window.addEventListener("keydown", activityListener);

		return () => {
			clearInterval(idleInterval.current!);
			clearInterval(countdownInterval.current!);
			window.removeEventListener("mousemove", activityListener);
			window.removeEventListener("keydown", activityListener);
		};
	}, [showDialog, location.pathname]);


	useEffect(() => {
		if (showDialog && countdown > 0) {
			countdownInterval.current = window.setInterval(() => {
				setCountdown((prev) => {
					if (prev === 1) {
						clearInterval(countdownInterval.current!);
						window.location.href = "/dashboard/sessionTimeOut";
						return 0;
					}
					return prev - 1;
				});
			}, 1000);
		}

		return () => {
			if (countdownInterval.current) {
				clearInterval(countdownInterval.current);
			}
		};
	}, [showDialog, countdown]);




	const handleFullScreen = () => {
		if (fullscreen) document.exitFullscreen();
		else document.body.requestFullscreen();
		setFullScreen(!fullscreen);
	};

	// [Context Api]
	const [isNull, setIsNull] = useState(true);
	const [lastSync, setLastSync] = useState(synced.fromNow());
	const value = { isNull, setIsNull, lastSync, setLastSync };

	useEffect(() => {
		setDateString(moment().tz("America/Chicago").format('ddd, DD MMM YYYY, hh:mm A'));

	}, []);

	// check if session is still valid
	useEffect(() => {
		// Initial Authentication Check
		let isRedirectToLogin = false;

		if (location.pathname === '/userLogin') {
			localStorage.setItem('sso-login', 'false');
			setAuthType(dispatch, 'local');
		}
		if (location.pathname === '/') {
			localStorage.setItem('sso-login', 'true');
			setAuthType(dispatch, 'sso');
		}

		let authenticated = false;

		if (isAuthenticated) {
			if (accessToken?.exp) {
				const currentTime = Math.floor(Date.now() / 1000);
				// Check if access token is expired
				if (accessToken.exp >= currentTime) {
					authenticated = true;
				} else {
					setAuthenticated(dispatch, false);
					setAuthToken(dispatch, null);
				}
			}
		}

		if (!authenticated) {
			const auth = getDecodedToken();
			if (auth) {
				const currentTime = Math.floor(Date.now() / 1000);
				if (auth.exp >= currentTime) {
					setAuthType(dispatch, 'local');
					setAuthToken(dispatch, auth);
					setAuthenticated(dispatch, true);
				} else {
					isRedirectToLogin = true;
				}
			} else {
				isRedirectToLogin = true;
			}

			if (isRedirectToLogin) {
				redirectToLogin();
			}
		}

		eventEmitter.on('showSnackbar', (message: string, severity: Severity, subMessgae?: any) => {
			setSnackbarMessage(message || '');
			setDataCount(subMessgae);
			setSnackbarSeverity(severity);
			setSnackbarOpen(true);
		});
	}, []);

	// const checkSession = async (): Promise<void> => {
	// 	axios
	// 		.get(`${url}/user/me`)
	// 		.then((response) => {
	// 			if (response.status === 200) {
	// 				const { success } = response.data;
	// 				if (success !== true) {
	// 					console.log("redirecting to sso login from App.tsx")
	// 					window.location.href = '/sso/login';
	// 				}
	// 			}
	// 		})
	// 		.catch((error) => {
	// 			console.error('Login error:', error);
	// 		});
	// };

	const handleCloseSnackbar = () => {
		setSnackbarOpen(false);
	};

	useEffect(() => {
		if (isAuthenticated) {
			const auth = getDecodedToken();
			console.log('Auth:', JSON.stringify(auth));
			if (auth) {
				const capabilities: string[] =
					auth.role?.reduce((acc: string[], role: any) => {
						const roleCapabilities = role.capabilities || [];
						roleCapabilities.forEach((capability: string) => {
							if (!acc.includes(capability)) {
								acc.push(capability);
							}
						});
						return acc;
					}, [] as string[]) || [];

				const roleNames = Array.isArray(auth.role)
					? auth.role.map((role: any) => role.name)
					: auth.role
						? [auth.role.name]
						: [];
				console.log(`Roles: ${roleNames}`)
				console.log(`Capabilities: ${capabilities}`)
				// Set the updated auth details
				setRole(dispatch, roleNames);
				setCapabilities(dispatch, capabilities);
				if (auth.privileges?.finops) {
					setPrivilege(dispatch, auth.privileges.finops);
				}
				setSector(dispatch, auth.sector ?? null);
				setSectorViewer(dispatch, auth.sectorViewer ?? []);
			}
		}
	}, [isAuthenticated]);

	useEffect(() => {
		if (dataFetchedRef.current || !isAuthenticated) return;
		dataFetchedRef.current = true;
		// listenToEventStream(); // Start listening to event stream
		redirectAfterLogin();
		fetchAlerts();
		const apiCallInterval = 10 * 60 * 1000; // 10 minutes in milliseconds
		const intervalId = setInterval(fetchAlerts, apiCallInterval);
		return () => clearInterval(intervalId);
	}, [isAuthenticated]);

	const [totalObservationCount, setTotalObservationCount] = useState(-1);

	const calculateTotalObservation = (data: any) => {
		const totalCount = data.observations.reduce((count: any, item: any) => count + item.totalObservation, 0);
		setTotalObservationCount(totalCount);
	};

	async function fetchAlerts() {
		const res = await getAlerts();
		if (res.success) {
			const newData = res.data;
			setOriginalRuleEngineData((prevData: any) => [...prevData, newData]);
			calculateTotalObservation(newData);
		}
	}

	// const listenToEventStream = () => {
	// 	const eventSource = new EventSource(`${url}${endpoints.notificationAlerts}`);

	// 	eventSource.onmessage = (event) => {
	// 		const newData = JSON.parse(event.data);
	// 		setOriginalRuleEngineData((prevData: any) => [...prevData, newData]);
	// 		calculateTotalObservation(newData);
	// 	};

	// 	eventSource.onerror = (error) => {
	// 		console.error('EventSource failed:', error);
	// 		eventSource.close();
	// 		listenToEventStream();
	// 	};
	// };

	const togglePanel = () => {
		setPanelOpen(!isPanelOpen);
	};

	const alertIconClass = `${location.pathname === '/LandingPage' ? 'landing-common-notification' : 'common-notification'
		} badge_box`;

	const showFullScreen = () => {
		return (
			location.pathname !== '/LandingPage' &&
			location.pathname !== '/dashboard/dataOpsLanding' &&
			location.pathname !== '/dashboard/financeOpsLanding' &&
			location.pathname !== '/dashboard/D&AIOpsLanding' &&
			location.pathname !== '/dashboard/programExecutiveLanding' &&
			location.pathname !== '/dashboard/platformCostLanding' &&
			location.pathname !== '/dashboard/alertKpiLanding' &&
			location.pathname !== '/dashboard/sessionTimeOut' &&
			location.pathname !== '/dashboard/monthlyXOpsLanding' &&
			location.pathname !== '/dashboard/xops-intelligence' && (
				<OverflowTooltip customTooltip={fullscreen ? 'Exit Full Screen' : 'Full Screen'}>
					<IconButton
						onClick={() => {
							handleFullScreen();
						}}
						// className={fullscreen ? 'exitFullScrrenBtn' : 'fullScrrenBtn'}
						sx={{
							cursor: 'pointer',
							zIndex: 100,
						}}
					>
						{fullscreen ? <ExitFullScreenIcon /> : <FullScreenIcon />}
					</IconButton>
				</OverflowTooltip>
			)
		);
	};

	const showSchedulerHealthlabel = () => {
		return (
			(location.pathname == '/data-pipeline/executiveOps' || location.pathname == '/data-pipeline/executiveOps/flna' || location.pathname == '/data-pipeline/executiveOps/pfna' || location.pathname == '/data-pipeline/executiveOps/emea' || location.pathname == '/data-pipeline/executiveTemplate') && (
				<Typography variant="subtitle1" className='sch-health-placeholder' color="light.diff" style={{ color: 'rgba(169, 173, 203, 1)', fontWeight: '600', fontSize: '12px', marginTop:'76px' }}>SCHEDULER HEALTH: <span style={{ marginLeft: '5px' }}>COMING SOON <Tooltip title={
					<><Typography variant="body2" color="light.diff" style={{ fontSize: '13px', color: 'rgba(169, 173, 203, 1)', fontWeight: '500', fontStyle: 'italic' }}>Est. Arrival</Typography>
						<Typography variant="body2" color="inherit" style={{ fontSize: '12px' }}>Aug 30, 2025</Typography>
					</>
				} arrow><ComingSoonIcon style={{ verticalAlign: 'text-bottom' }} /></Tooltip></span></Typography>
			)
		)
	}

	const showSchedulerHealthlabelAPAC = () => {
		return (
			(location.pathname == '/data-pipeline/executiveOps/apac') && (
				<Typography variant="subtitle1" className='sch-health-placeholder' color="light.diff" style={{ color: 'rgba(169, 173, 203, 1)', fontWeight: '600', fontSize: '12px', marginTop:'76px' }}>SCHEDULER HEALTH: <span style={{ marginLeft: '5px' }}>COMING SOON <Tooltip title={
					<><Typography variant="body2" color="light.diff" style={{ fontSize: '13px', color: 'rgba(169, 173, 203, 1)', fontWeight: '500', fontStyle: 'italic' }}>Est. Arrival</Typography>
						<Typography variant="body2" color="inherit" style={{ fontSize: '12px' }}>Aug 30, 2025</Typography>
					</>
				} arrow><ComingSoonIcon style={{ verticalAlign: 'text-bottom' }} /></Tooltip></span></Typography>
			)
		)
	}

	const showCSTtimelabel = () => {
		return (
			location.pathname == '/data-pipeline/executiveOps' && (<Typography style={{ color: 'rgba(113, 117, 143, 1)', fontWeight: '500', fontSize: '14px' }} variant="subtitle1" className='time-placeholder' color="light.diff">{dateString}</Typography>)
		);
	}

	const showLandingPageNotification = () => {
		return (
			location.pathname !== '/evidently-mlobs-poc' &&
			location.pathname !== '/' &&
			location.pathname !== '/userLogin' &&
			// location.pathname === '/LandingPage' && 
			(
				<Box>
					<Box
						component="span"
						sx={{
							cursor: totalObservationCount > 0 ? 'pointer' : 'default',
							zIndex: 10000,
						}}
						onClick={() => {
						if (totalObservationCount > 0) togglePanel();
						}}
					>
						<Tooltip arrow title="Rule Observations" TransitionComponent={Zoom}>
						<Badge
							badgeContent={totalObservationCount >= 0 ? totalObservationCount : '...'}
							max={9999}
							showZero
							sx={{
							'& .MuiBadge-badge': {
								backgroundColor: totalObservationCount > 0 ? '#ED1c24' : '#ED1c24',
								color: totalObservationCount > 0 ? '#fff' : '#fff',
								fontFamily: 'Montserrat',
								fontWeight: 600,
								borderRadius: '8px !important',
							}
							}}
						>
							<Box component="img" src={NotificationIcon} />
						</Badge>
						</Tooltip>
					</Box>
				</Box>
			)
		);
	};

	// useEffect(() => {}, [isAuthenticated]);

	const showHeaderAndNav = () => {
		return (
			isAuthenticated &&
			location.pathname !== '/' &&
			location.pathname !== '/userLogin' &&
			location.pathname !== '/dashboard/sessionTimeOut' && 
			(
				<>
					<Suspense fallback={<div>Loading...</div>}>
						{/* <Sidebar /> */}
						<GlobalSidebar />
					</Suspense>
					{/* <Suspense fallback={<div>Loading...</div>}>
						<Header notificationData={originalRuleEngineData} />
					</Suspense> */}
				</>
			)
		);
	};

	const showGlobalHeader = () => {
		return (
			isAuthenticated &&
			location.pathname !== '/' &&
			location.pathname !== '/userLogin' &&
			(
				<>
					<Suspense fallback={<div>Loading...</div>}>
						<GlobalHeader memberData={memberData} notificationData={originalRuleEngineData} showLandingPageNotification={showLandingPageNotification} showFullScreen={showFullScreen}/>
					</Suspense>
				</>
			)
		);
	};

	return (
		<DndProvider backend={HTML5Backend}>
			<HeaderContext.Provider value={value}>

				{/* {showSchedulerHealthlabel()}  to be used in the future */}

				{/* {showSchedulerHealthlabelAPAC()}  to be used in the future */}

				{showCSTtimelabel()}

				{/* {showFullScreen()} */}

				{isPanelOpen && <RulesEngine close={togglePanel} ruledata={originalRuleEngineData} />}

				{showHeaderAndNav()}

				{showGlobalHeader()}

				<SideDrawer />
				{isAuthenticated &&
					location.pathname !== '/' &&
					location.pathname !== '/userLogin' &&
					location.pathname !== '/dashboard/sessionTimeOut' &&
					!location.pathname.toLowerCase().includes('finops') && (
						<AskMeButton />
				)}
				{isAuthenticated && location.pathname.toLowerCase().includes('finops') && (
					<GenieChatButton />
				)}
				{(location.pathname.includes('finops/resourcemapping/search')
					|| location.pathname.includes('finops/resourcemapping/projects')
					|| (location.pathname.includes('finops/resourcemapping/programs')
						&& snackbarMessage.includes('Service temporarily unavailable')) ||
						location.pathname.includes('finops/resourcemapping/costsummary'))
					? (
						<Snackbar
							anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
							open={snackbarOpen}
							autoHideDuration={((location.pathname.includes('finops/resourcemapping/projects') 
								&&  ['linked', 'allocated', 'Queries Committed', 'Query deleted', 'Queryname', 'Request was canceled'].some(msg => snackbarMessage?.includes(msg)))
								|| (location.pathname.includes('finops/resourcemapping/search') && (['Request was canceled'].some(msg => snackbarMessage?.includes(msg))))) 
								? 6000 : undefined}
							onClose={handleCloseSnackbar} className='bg-resource'
						>
							<Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} 
								className= {['linked', 'allocated', 'Queries Committed', 'Query deleted', 'Queryname'].some(msg => snackbarMessage?.includes(msg)) ? 'success-resource' : 'alert-resource'}>
								<Typography style={{ fontSize: '16px', fontWeight: '700', marginBottom: '4px', color: 
									['Queries Committed'].some(msg => snackbarMessage?.includes(msg)) ? '#fd9532': '' }}>
									{snackbarSeverity === 'info' && 'Alert Message'}
									{snackbarSeverity === 'success' && ['Query deleted'].some(msg => snackbarMessage?.includes(msg)) && 'Query Deleted'}
									{snackbarSeverity === 'success' && ['Queryname has been saved'].some(msg => snackbarMessage?.includes(msg)) && 'Query Saved'}
									{snackbarSeverity === 'success' && ['Queryname has been updated'].some(msg => snackbarMessage?.includes(msg)) && 'Query Updated'}
									{snackbarSeverity === 'success' && ['Query linked'].some(msg => snackbarMessage?.includes(msg)) && 'Query Linked'}
									{snackbarSeverity === 'success' && ['unlinked'].some(msg => snackbarMessage?.includes(msg)) && 'Query Unlinked'}
									{snackbarSeverity === 'success' && ['allocated'].some(msg => snackbarMessage?.includes(msg)) && 'Resource Allocated'}
								</Typography>
								<Stack sx={{ backgroundColor: '#ffffff !important' }}>
									{snackbarMessage
										.split('.')
										.map((sentence, index) =>
											sentence.trim() ? (
												<Typography key={index} style={{ fontSize: '14px', fontWeight: '400', 
													color: ['Queries Committed'].some(msg => sentence?.includes(msg)) ? '#01C008' : '#000000' }}>
													{sentence.trim()}
												</Typography>
											) : null
										)}
									{dataCount && (
										<Typography variant="caption" color="#000000">
											{dataCount}
										</Typography>
									)}
								</Stack>
							</Alert>
						</Snackbar>
					) : ((location.pathname.includes('finops/resourcemapping/programs')
						&& (snackbarMessage.includes('duplicated') || snackbarMessage.includes('Project deleted successfully')))
					? (
						<Snackbar
							anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
							open={snackbarOpen}
							autoHideDuration={6000}
							onClose={handleCloseSnackbar} className='bg-resource'
						>
							<Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} className='success-resource'>
								<Typography style={{ fontSize: '16px', fontWeight: '700', marginBottom: '4px' }}>
									{snackbarSeverity === 'success' && snackbarMessage.includes('duplicated') && 'Project Duplicated'}
									{snackbarSeverity === 'success' && snackbarMessage.includes('Project deleted successfully') && 'Project Deleted'}
								</Typography>
								<Stack sx={{ backgroundColor: '#ffffff !important' }}>
									{snackbarMessage
										.split('.')
										.map((sentence, index) =>
											sentence.trim() ? (
												<Typography key={index} style={{ fontSize: '14px', fontWeight: '400', color: '#000000' }}>
													{sentence.trim()}
												</Typography>
											) : null
										)}
								</Stack>
							</Alert>
						</Snackbar>
					) : (
						!(location.pathname.includes('finops/resourcemapping/programs') &&
	  						snackbarMessage.includes('This project name is already present in this program')) && (
						<Snackbar
							anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
							open={snackbarOpen}
							autoHideDuration={6000}
							onClose={handleCloseSnackbar}
						>
							<Alert onClose={handleCloseSnackbar} severity={snackbarSeverity}>
								<Typography style={{ fontSize: '16px', fontWeight: '700', marginBottom: '4px' }}>
									{snackbarSeverity === 'info' && 'Alert Message'}
									{snackbarSeverity === 'success' && 'Success Message'}
									{snackbarSeverity === 'warning' && 'Warning Message'}
									{snackbarSeverity === 'error' && 'Error Message'}
								</Typography>
								<Stack>
									{snackbarMessage
										.split('.')
										.map((sentence, index) =>
											sentence.trim() ? (
												<Typography key={index} style={{ fontSize: '14px', fontWeight: '400' }}>
													{sentence.trim()}
												</Typography>
											) : null
										)}
								</Stack>
							</Alert>
						</Snackbar>)
					))}
				{showDialog && getToken() && (
					<SessionTimeoutDialog
						open={showDialog}
						onClose={handleCloseLogout}
						idleout={idleout}
						onStayLoggedIn={stayLoggedIn}
						countdown={countdown}
					/>
				)}

				{!isAuthenticated && (
					<Routes>
						<Route path="/" element={<Login />} />

						<Route path="/userLogin" element={<FormLogin />} />
					</Routes>
				)}

				{isAuthenticated && (
					<aside className={isAuthenticated ? 'main-content' : ''}>
						{/* {showLandingPageNotification()} */}
						<div className="parent-container">
							<Suspense fallback={<div>Loading...</div>}>
								<AppRoutes />
							</Suspense>
						</div>
					</aside>
				)}
				{/* <Footer /> */}
			</HeaderContext.Provider>
		</DndProvider>
	);
}

export default App;
